import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./WishlistPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart as faSolidHeart,
  faTrash,
  faShoppingCart,
  faEye,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAuth } from "../../context/AuthContext";
import { getWishlistProducts } from "../../service/product";
import { useCart } from "../../context/CartContext";
import WishlistProductCard from "./WishlistProductCard";

const WishlistPage = () => {
  const [wishlistProducts, setWishlistProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    fetchWishlistProducts();
  }, []);

  const fetchWishlistProducts = async () => {
    try {
      setLoading(true);
      const products = await getWishlistProducts();
      setWishlistProducts(products.data);
    } catch (err: any) {
      setError(err.message || "Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId: string) => {
    try {
      // await removeFromWishlist(productId);
      setWishlistProducts((prev) =>
        prev.filter((product) => product._id !== productId)
      );
    } catch (err: any) {
      setError(err.message || "Failed to remove product from wishlist");
    }
  };
  const handleMoveToCart = (product: any) => {
    // Implementation for moving to cart
    addToCart({ ...product, quantity: 1 });
    // You would typically dispatch an action to your cart context here
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <div className={styles.errorMessage}>{error}</div>
        <button className={styles.retryButton} onClick={fetchWishlistProducts}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={styles.wishlistPage}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroBackground}></div>
        <div className={styles.heroContent}>
          <motion.div
            className={styles.heroBadge}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <FontAwesomeIcon icon={faSolidHeart} /> Your Favorites
          </motion.div>
          <h1 className={styles.heroTitle}>
            <span className={styles.gradientText}>Your Wishlist</span>
          </h1>
          <p className={styles.heroSubtitle}>
            All the products you've loved and saved for later
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Breadcrumb Navigation */}
        <div className={styles.breadcrumb}>
          <Link to="/" className={styles.breadcrumbLink}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
          </Link>
        </div>

        {/* Page Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <h2>Your Saved Items</h2>
            <p>
              {wishlistProducts.length}{" "}
              {wishlistProducts.length === 1 ? "product" : "products"} in your
              wishlist
            </p>
          </div>
        </div>

        {/* Wishlist Content */}
        {wishlistProducts.length === 0 ? (
          <div className={styles.emptyWishlist}>
            <div className={styles.emptyIcon}>
              <FontAwesomeIcon icon={faSolidHeart} />
            </div>
            <h3>Your wishlist is empty</h3>
            <p>
              Start saving your favorite products by clicking the heart icon
            </p>
            <Link to="/gallery" className={styles.browseButton}>
              Browse Products
            </Link>
          </div>
        ) : (
          <div className={styles.wishlistGrid}>
            <AnimatePresence>
              {wishlistProducts.map((product) => (
                <WishlistProductCard
                  key={product._id}
                  product={product}
                  onRemove={() => handleRemoveFromWishlist(product._id)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
